import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Info, KeyRound, UserPlus } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorMessage } from "@/components/ui/error-message";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { api, type Paginated } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { Company, Employee, OrgRef } from "@/lib/types";

import { ResourceCombobox } from "./org-resource-pickers";
import {
  listQueryKey,
  type OrgResourceKind,
} from "./org-resource-config";

const today = () => new Date().toISOString().slice(0, 10);

const schema = z.object({
  company: z.string().uuid("Pick a company."),
  // Optional FKs (departments / branches etc. may legitimately be empty
  // for some employees; backend allows null).
  department: z.string().uuid().or(z.literal("")).optional().default(""),
  branch: z.string().uuid().or(z.literal("")).optional().default(""),
  designation: z.string().uuid().or(z.literal("")).optional().default(""),
  employment_type: z.string().uuid().or(z.literal("")).optional().default(""),
  grade: z.string().uuid().or(z.literal("")).optional().default(""),

  employee_id: z.string().max(32).optional().default(""),
  first_name: z.string().min(1, "First name is required.").max(64),
  middle_name: z.string().max(64).optional().default(""),
  last_name: z.string().min(1, "Last name is required.").max(64),
  suffix: z.string().max(16).optional().default(""),
  preferred_name: z.string().max(64).optional().default(""),

  gender: z.string().max(24).optional().default(""),
  civil_status: z.string().max(16).optional().default(""),
  nationality: z.string().max(64).optional().default("Filipino"),
  date_of_birth: z.string().optional().default(""),

  date_of_joining: z.string().min(1, "Date of joining is required."),
  status: z.string().min(1).default("active"),
  salary_currency: z.string().min(1).default("PHP"),
  salary_mode: z.string().min(1).default("monthly"),
  monthly_basic: z.coerce.number().nonnegative("Must be ≥ 0."),

  personal_email: z.string().email().or(z.literal("")).optional().default(""),
  company_email: z.string().email().or(z.literal("")).optional().default(""),
  mobile_personal: z.string().max(32).optional().default(""),
  mobile_company: z.string().max(32).optional().default(""),

  sss_number: z.string().max(32).optional().default(""),
  tin: z.string().max(32).optional().default(""),
  philhealth_number: z.string().max(32).optional().default(""),
  hdmf_number: z.string().max(32).optional().default(""),

  // Provision a login on create (username = employee number).
  auto_create_user_on_hire: z.boolean().default(false),
  // Optional admin-set initial password; blank → auto-generate.
  initial_password: z.string().optional().default(""),
});

type FormValues = z.infer<typeof schema>;

interface ProvisionedUser {
  username: string;
  temporary_password: string | null;
  must_change_password: boolean;
}

type CreateResult = Employee & { provisioned_user?: ProvisionedUser };

interface CreateEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY: FormValues = {
  company: "",
  department: "",
  branch: "",
  designation: "",
  employment_type: "",
  grade: "",
  employee_id: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  suffix: "",
  preferred_name: "",
  gender: "",
  civil_status: "",
  nationality: "Filipino",
  date_of_birth: "",
  date_of_joining: today(),
  status: "active",
  salary_currency: "PHP",
  salary_mode: "monthly",
  monthly_basic: 0,
  personal_email: "",
  company_email: "",
  mobile_personal: "",
  mobile_company: "",
  sss_number: "",
  tin: "",
  philhealth_number: "",
  hdmf_number: "",
  auto_create_user_on_hire: false,
  initial_password: "",
};

export function CreateEmployeeDialog({
  open,
  onClose,
}: CreateEmployeeDialogProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [values, setValues] = useState<FormValues>(EMPTY);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormValues, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [provisioned, setProvisioned] = useState<ProvisionedUser | null>(null);

  const companies = useQuery({
    queryKey: ["companies", "all"],
    queryFn: () =>
      api.get<Paginated<Company>>(
        "/organization/companies/?is_active=true&page_size=50",
      ),
    enabled: open,
  });

  // Query keys must match `listQueryKey()` in org-resource-config so that
  // the picker's invalidation refreshes the same cache entries.
  const departments = useFkQuery("department", values.company, open);
  const branches = useFkQuery("branch", values.company, open);
  const designations = useFkQuery("designation", values.company, open);
  const employmentTypes = useFkQuery("employment_type", values.company, open);
  const grades = useFkQuery("grade", values.company, open);

  const selectedCompany = companies.data?.results.find(
    (c) => c.id === values.company,
  );

  useEffect(() => {
    if (
      open &&
      !values.company &&
      companies.data &&
      companies.data.results.length > 0
    ) {
      setValues((prev) => ({
        ...prev,
        company: companies.data.results[0].id,
      }));
    }
  }, [open, companies.data, values.company]);

  useEffect(() => {
    if (!open) {
      setValues(EMPTY);
      setErrors({});
      setSubmitError(null);
      setProvisioned(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (parsed: FormValues) => {
      // Strip empty-string FKs so the backend gets null instead of "".
      const payload: Record<string, unknown> = {
        ...parsed,
        monthly_basic: String(parsed.monthly_basic),
      };
      // FKs + nullable date: blank → null.
      for (const key of [
        "department",
        "branch",
        "designation",
        "employment_type",
        "grade",
        "date_of_birth",
      ] as const) {
        if (!payload[key]) payload[key] = null;
      }
      // employee_id is a CharField, not nullable. Sending null trips
      // "may not be null". The AutoMintIDMixin on EmployeeViewSet
      // pulls the next EMP-NNNNN from the company's NumberSeries when
      // the value is blank, so the empty string is what we want.
      if (!payload.employee_id) payload.employee_id = "";
      // Only send an admin-set password when a login is actually being
      // created and a value was typed — avoids validating a stray field.
      if (!parsed.auto_create_user_on_hire || !parsed.initial_password) {
        delete payload.initial_password;
      }
      return api.post<CreateResult>("/employees/employees/", payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.setQueryData(["employee", data.id], data);
      toast.success(`${data.full_name} added`, `Employee ID ${data.employee_id}`);
      // If a login was provisioned, reveal the one-time credentials before
      // closing — the temp password is shown only here, once.
      if (data.provisioned_user?.temporary_password) {
        setProvisioned(data.provisioned_user);
      } else {
        onClose();
      }
    },
    onError: (err) => setSubmitError(err),
  });

  function update<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const next: Partial<Record<keyof FormValues, string>> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof FormValues;
        if (k && !next[k]) next[k] = issue.message;
      }
      setErrors(next);
      return;
    }
    create.mutate(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="max-w-3xl overflow-hidden rounded-xl bg-white p-0"
    >
      {/* Top accent stripe — matches the mockup's blue→cyan gradient. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 z-10 h-[3px] bg-gradient-to-r from-blue to-cyan"
      />
      {provisioned ? (
        <ProvisionedView creds={provisioned} onDone={onClose} />
      ) : (
      <form onSubmit={handleSubmit} noValidate>
        <DialogHeader className="space-y-2 px-8 pb-4 pt-7">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-blue">
            <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
            Quick create · Employee
          </div>
          <DialogTitle className="text-[28px] font-bold leading-tight tracking-tight text-navy">
            New employee
          </DialogTitle>
          <p className="max-w-[540px] text-sm leading-relaxed text-slate">
            Creates an active employee record. Leave{" "}
            <CodeInline>Employee ID</CodeInline> blank to auto-mint the next{" "}
            <CodeInline>EMP-NNNNN</CodeInline> from the company's number
            series.
          </p>
        </DialogHeader>

        <DialogBody className="flex max-h-[60vh] flex-col gap-0 overflow-y-auto px-8 pb-2 pt-1">
          <Section title="Company">
            <Grid cols={2}>
              <FormField label="Company" required error={errors.company}>
                <Select
                  value={values.company}
                  onChange={(e) => update("company", e.target.value)}
                  disabled={companies.isLoading}
                >
                  <option value="" disabled>
                    {companies.isLoading ? "Loading…" : "Select a company"}
                  </option>
                  {companies.data?.results.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField
                label="Employee ID"
                helperText={
                  <span>
                    Leave blank to auto-mint <CodeInline>EMP-NNNNN</CodeInline>.
                  </span>
                }
              >
                <Input
                  value={values.employee_id ?? ""}
                  onChange={(e) => update("employee_id", e.target.value)}
                  placeholder="e.g. MGR-001"
                />
              </FormField>
            </Grid>
            <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-bg p-3">
              <Checkbox
                checked={values.auto_create_user_on_hire}
                onChange={(e) =>
                  update("auto_create_user_on_hire", e.target.checked)
                }
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-navy">
                  Create a login for this employee
                </span>
                <span className="mt-0.5 block text-xs text-slate">
                  Username is the employee number (e.g. 000053). They'll be
                  asked to change the password on first sign-in.
                </span>
              </span>
            </label>
            {values.auto_create_user_on_hire && (
              <div className="mt-3">
                <FormField
                  label="Initial password"
                  error={errors.initial_password}
                  helperText="Leave blank to auto-generate a one-time password. Either way, you'll see it once to share with the employee."
                >
                  <Input
                    type="text"
                    autoComplete="off"
                    value={values.initial_password ?? ""}
                    onChange={(e) => update("initial_password", e.target.value)}
                    placeholder="Auto-generate if blank"
                    className="font-mono"
                  />
                </FormField>
              </div>
            )}
          </Section>

          <Section title="Identity" hint="As shown on government ID">
            <Grid cols={3}>
              <FormField
                label="First name"
                required
                error={errors.first_name}
                className="sm:col-span-2"
              >
                <Input
                  value={values.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  placeholder="Juan"
                  autoComplete="given-name"
                />
              </FormField>
              <FormField label="Middle name">
                <Input
                  value={values.middle_name ?? ""}
                  onChange={(e) => update("middle_name", e.target.value)}
                  placeholder="Dela"
                  autoComplete="additional-name"
                />
              </FormField>
              <FormField
                label="Last name"
                required
                error={errors.last_name}
                className="sm:col-span-2"
              >
                <Input
                  value={values.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  placeholder="Cruz"
                  autoComplete="family-name"
                />
              </FormField>
              <FormField label="Suffix">
                <Input
                  value={values.suffix ?? ""}
                  onChange={(e) => update("suffix", e.target.value)}
                  placeholder="Jr., III"
                />
              </FormField>
              <FormField label="Preferred">
                <Input
                  value={values.preferred_name ?? ""}
                  onChange={(e) => update("preferred_name", e.target.value)}
                  placeholder="What they go by"
                />
              </FormField>
              <FormField label="Date of birth">
                <Input
                  type="date"
                  value={values.date_of_birth ?? ""}
                  onChange={(e) => update("date_of_birth", e.target.value)}
                />
              </FormField>
              <FormField label="Nationality">
                <Input
                  value={values.nationality ?? ""}
                  onChange={(e) => update("nationality", e.target.value)}
                />
              </FormField>
            </Grid>
          </Section>

          <Section title="Employment" hint="Optional at create · editable later">
            <Grid cols={2}>
              <FormField label="Department" error={errors.department}>
                <ResourceCombobox
                  kind="department"
                  companyId={values.company}
                  companyAbbreviation={selectedCompany?.code}
                  value={values.department ?? ""}
                  onChange={(id) => update("department", id)}
                  disabled={!values.company}
                  placeholder="Select department"
                  query={departments}
                />
              </FormField>
              <FormField label="Branch" error={errors.branch}>
                <ResourceCombobox
                  kind="branch"
                  companyId={values.company}
                  companyAbbreviation={selectedCompany?.code}
                  value={values.branch ?? ""}
                  onChange={(id) => update("branch", id)}
                  disabled={!values.company}
                  placeholder="Select branch"
                  query={branches}
                />
              </FormField>
              <FormField label="Designation" error={errors.designation}>
                <ResourceCombobox
                  kind="designation"
                  companyId={values.company}
                  companyAbbreviation={selectedCompany?.code}
                  value={values.designation ?? ""}
                  onChange={(id) => update("designation", id)}
                  disabled={!values.company}
                  placeholder="Select designation"
                  query={designations}
                />
              </FormField>
              <FormField label="Employment type" error={errors.employment_type}>
                <ResourceCombobox
                  kind="employment_type"
                  companyId={values.company}
                  companyAbbreviation={selectedCompany?.code}
                  value={values.employment_type ?? ""}
                  onChange={(id) => update("employment_type", id)}
                  disabled={!values.company}
                  placeholder="Select type"
                  query={employmentTypes}
                />
              </FormField>
              <FormField label="Grade" error={errors.grade}>
                <ResourceCombobox
                  kind="grade"
                  companyId={values.company}
                  companyAbbreviation={selectedCompany?.code}
                  value={values.grade ?? ""}
                  onChange={(id) => update("grade", id)}
                  disabled={!values.company}
                  placeholder="Select grade"
                  query={grades}
                />
              </FormField>
              <FormField
                label="Date of joining"
                required
                error={errors.date_of_joining}
              >
                <Input
                  type="date"
                  value={values.date_of_joining}
                  onChange={(e) => update("date_of_joining", e.target.value)}
                />
              </FormField>
            </Grid>
          </Section>

          <Section title="Salary" hint="Payroll engine pulls from these on first run">
            <Grid cols={3}>
              <FormField label="Status">
                <Select
                  value={values.status}
                  onChange={(e) => update("status", e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="on_leave">On leave</option>
                  <option value="suspended">Suspended</option>
                </Select>
              </FormField>
              <FormField label="Salary mode">
                <Select
                  value={values.salary_mode}
                  onChange={(e) => update("salary_mode", e.target.value)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="daily">Daily</option>
                  <option value="hourly">Hourly</option>
                </Select>
              </FormField>
              <FormField label="Currency">
                <Input
                  value={values.salary_currency}
                  onChange={(e) => update("salary_currency", e.target.value)}
                  className="font-mono"
                />
              </FormField>
              <FormField
                label="Monthly basic"
                error={errors.monthly_basic}
                helperText="Stored as decimal; payroll multiplies by salary mode."
                className="sm:col-span-3"
              >
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={values.monthly_basic}
                  onChange={(e) =>
                    update("monthly_basic", Number(e.target.value))
                  }
                  className="tabular-nums"
                />
              </FormField>
            </Grid>
          </Section>

          <Section title="Contact">
            <Grid cols={2}>
              <FormField label="Personal email" error={errors.personal_email}>
                <Input
                  type="email"
                  value={values.personal_email ?? ""}
                  onChange={(e) => update("personal_email", e.target.value)}
                  autoComplete="email"
                />
              </FormField>
              <FormField label="Company email" error={errors.company_email}>
                <Input
                  type="email"
                  value={values.company_email ?? ""}
                  onChange={(e) => update("company_email", e.target.value)}
                />
              </FormField>
              <FormField label="Mobile (personal)">
                <Input
                  value={values.mobile_personal ?? ""}
                  onChange={(e) => update("mobile_personal", e.target.value)}
                  autoComplete="tel"
                />
              </FormField>
              <FormField label="Mobile (company)">
                <Input
                  value={values.mobile_company ?? ""}
                  onChange={(e) => update("mobile_company", e.target.value)}
                />
              </FormField>
            </Grid>
          </Section>

          <Section title="PH statutory" hint="Editable from the employee's detail page">
            <Grid cols={4}>
              <FormField label="SSS">
                <Input
                  value={values.sss_number ?? ""}
                  onChange={(e) => update("sss_number", e.target.value)}
                  className="font-mono"
                />
              </FormField>
              <FormField label="TIN">
                <Input
                  value={values.tin ?? ""}
                  onChange={(e) => update("tin", e.target.value)}
                  className="font-mono"
                />
              </FormField>
              <FormField label="PhilHealth">
                <Input
                  value={values.philhealth_number ?? ""}
                  onChange={(e) =>
                    update("philhealth_number", e.target.value)
                  }
                  className="font-mono"
                />
              </FormField>
              <FormField label="HDMF">
                <Input
                  value={values.hdmf_number ?? ""}
                  onChange={(e) => update("hdmf_number", e.target.value)}
                  className="font-mono"
                />
              </FormField>
            </Grid>
          </Section>

          <ErrorMessage error={submitError} className="pt-2" />
        </DialogBody>

        <DialogFooter className="items-center justify-between gap-4 border-t border-slate-200 bg-bg px-8 py-4 sm:flex-row">
          <p className="flex items-center gap-1.5 text-xs text-slate">
            <Info className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
            Government IDs and salary structure can be refined after creation
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Create employee"}
            </Button>
          </div>
        </DialogFooter>
      </form>
      )}
    </Dialog>
  );
}

/** One-time credentials reveal shown right after a login is provisioned. */
function ProvisionedView({
  creds,
  onDone,
}: {
  creds: ProvisionedUser;
  onDone: () => void;
}) {
  return (
    <div>
      <DialogHeader className="space-y-2 px-8 pb-4 pt-7">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-green-600">
          <KeyRound className="h-3.5 w-3.5" strokeWidth={2} />
          Login created
        </div>
        <DialogTitle className="text-[24px] font-bold leading-tight tracking-tight text-navy">
          Share these sign-in details
        </DialogTitle>
        <p className="max-w-[540px] text-sm leading-relaxed text-slate">
          The temporary password is shown <strong>once</strong> — copy it now.
          The employee must change it on first sign-in.
        </p>
      </DialogHeader>
      <DialogBody className="px-8 pb-2 pt-1">
        <div className="space-y-3">
          <CredRow label="Username" value={creds.username} />
          <CredRow
            label="Temporary password"
            value={creds.temporary_password ?? ""}
            mono
          />
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-slate">
          <Info className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
          They can sign in at the login page and will be prompted to set a new
          password.
        </p>
      </DialogBody>
      <DialogFooter className="justify-end border-t border-slate-200 bg-bg px-8 py-4">
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

function CredRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <p
          className={cn(
            "truncate text-sm text-navy",
            mono && "font-mono tracking-tight",
          )}
        >
          {value}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={copy}
        aria-label={`Copy ${label.toLowerCase()}`}
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-600" strokeWidth={2} />
        ) : (
          <Copy className="h-4 w-4" strokeWidth={2} />
        )}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

/* ─────────────────────────── local primitives ────────────────────────── */

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-b border-slate-100 py-5 last:border-b-0">
      <div className="mb-4 flex items-baseline justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate">
          {title}
        </h3>
        {hint ? (
          <span className="text-[11px] text-slate-400">{hint}</span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function Grid({
  cols,
  children,
}: {
  cols: 2 | 3 | 4;
  children: React.ReactNode;
}) {
  const cls = cn(
    "grid gap-4",
    cols === 2 && "grid-cols-1 sm:grid-cols-2",
    cols === 3 && "grid-cols-2 sm:grid-cols-3",
    cols === 4 && "grid-cols-2 sm:grid-cols-4",
  );
  return <div className={cls}>{children}</div>;
}

function CodeInline({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-navy">
      {children}
    </code>
  );
}

/** Per-resource list query, keyed to match `org-resource-config.listQueryKey`. */
function useFkQuery(kind: OrgResourceKind, companyId: string, enabled: boolean) {
  const endpointByKind: Record<OrgResourceKind, string> = {
    department: "/organization/departments/",
    branch: "/organization/branches/",
    designation: "/organization/designations/",
    employment_type: "/organization/employment-types/",
    grade: "/organization/grades/",
  };
  return useQuery({
    queryKey: listQueryKey(kind, companyId),
    queryFn: () =>
      api.get<Paginated<OrgRef>>(
        `${endpointByKind[kind]}?company=${companyId}&page_size=100`,
      ),
    enabled: enabled && !!companyId,
  });
}
